import React, { useState, useEffect } from 'react';
import { Card, List, Tag, Button, Typography, Space, Badge, Spin } from 'antd';
import { 
  InboxOutlined, 
  ClockCircleOutlined, 
  ExclamationCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const { Title, Text } = Typography;

const WorkBasketWidget = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workItems, setWorkItems] = useState([]);
  const [stats, setStats] = useState({});

  // Fetch recent work items
  const fetchRecentWorkItems = async () => {
    try {
      setLoading(true);
      const [itemsResponse, statsResponse] = await Promise.all([
        axios.get('/work-items/my-basket?limit=5&status=Pending&status=In Progress&status=Overdue'),
        axios.get('/work-items/stats')
      ]);
      
      setWorkItems(itemsResponse.data.data.workItems || []);
      setStats(statsResponse.data.data.stats || {});
    } catch (error) {
      console.error('Error fetching work basket widget data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentWorkItems();
  }, []);

  // Get icon for work item type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'Order':
        return '📋';
      case 'EncounterNote':
        return '📝';
      case 'Message':
        return '💬';
      case 'LabResult':
        return '🧪';
      default:
        return '📄';
    }
  };

  // Get color for priority
  const getPriorityColor = (priority) => {
    return priority === 'Urgent' ? 'red' : 'blue';
  };

  // Get color for status
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return 'orange';
      case 'In Progress':
        return 'blue';
      case 'Overdue':
        return 'red';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Card title="My Work Basket" style={{ height: '400px' }}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <InboxOutlined />
          My Work Basket
          {(stats.pendingItems > 0 || stats.overdueItems > 0) && (
            <Badge 
              count={stats.pendingItems + stats.overdueItems} 
              style={{ backgroundColor: stats.overdueItems > 0 ? '#f5222d' : '#faad14' }}
            />
          )}
        </Space>
      }
      extra={
        <Button 
          type="link" 
          icon={<ArrowRightOutlined />}
          onClick={() => navigate('/work-basket')}
        >
          View All
        </Button>
      }
      style={{ height: '400px' }}
    >
      {/* Quick Stats */}
      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
        <Space size="large">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#faad14' }}>
              {stats.pendingItems || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>Pending</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f5222d' }}>
              {stats.overdueItems || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>Overdue</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f5222d' }}>
              {stats.urgentItems || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>Urgent</div>
          </div>
        </Space>
      </div>

      {/* Recent Work Items */}
      {workItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <InboxOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
          <div>No pending work items</div>
          <div style={{ fontSize: '12px' }}>Great job! Your work basket is clear.</div>
        </div>
      ) : (
        <List
          size="small"
          dataSource={workItems}
          renderItem={(item) => (
            <List.Item
              style={{ 
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <Space size="small" style={{ marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px' }}>{getTypeIcon(item.type)}</span>
                      <Tag color={getPriorityColor(item.priority)} size="small">
                        {item.priority}
                      </Tag>
                      <Tag color={getStatusColor(item.status)} size="small">
                        {item.status}
                      </Tag>
                    </Space>
                    
                    <div style={{ marginBottom: '4px' }}>
                      <Text strong style={{ fontSize: '13px' }}>
                        {item.title.length > 40 ? `${item.title.substring(0, 40)}...` : item.title}
                      </Text>
                    </div>
                    
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      <Space size="small">
                        <span>
                          {item.patient?.firstName} {item.patient?.lastName}
                        </span>
                        <span>•</span>
                        <span>
                          {moment(item.createdAt).fromNow()}
                        </span>
                      </Space>
                    </div>
                  </div>
                  
                  {item.status === 'Overdue' && (
                    <ExclamationCircleOutlined 
                      style={{ color: '#f5222d', fontSize: '16px' }} 
                    />
                  )}
                  {item.priority === 'Urgent' && item.status !== 'Overdue' && (
                    <ClockCircleOutlined 
                      style={{ color: '#faad14', fontSize: '16px' }} 
                    />
                  )}
                </div>
              </div>
            </List.Item>
          )}
        />
      )}
      
      {workItems.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Button 
            type="primary" 
            size="small"
            onClick={() => navigate('/work-basket')}
          >
            View All Work Items ({stats.totalItems || 0})
          </Button>
        </div>
      )}
    </Card>
  );
};

export default WorkBasketWidget;
